import { CancelBtn } from 'components/Button/CancelBtn'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Input } from 'components/Input/Input'
import { NavBar } from 'components/NavBar/NavBar'
import {
  clearFatigueRules,
  clearSingleFatigueRule,
  createFatigueRule,
  getFatigueRules,
  updateFatigueRule,
} from 'features/operations/fatigueSlice'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Button, Flex, H1WithBack, ItemsGrid, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IGetAllFatigueRulesSingleResponse } from 'types/api/operationsFatigueApi.interface'
import { IFatigueRuleState } from 'types/fatigueRules.interface'
import { formatDateForDisplay, getParams } from 'utils/helpers'
import { DEFAULT_FATIGUE_RULE_FORM } from './fatigueRulesData'
import { Badge } from 'components/Badge/Badge'

const formatRuleSummary = (rule: IGetAllFatigueRulesSingleResponse) =>
  `${rule.maxHoursPerShift}h/shift · ${rule.maxHoursPer7Days}h/7d · rest ≥ ${rule.minRestHours}h`

const mapRuleToForm = (rule: IGetAllFatigueRulesSingleResponse): IFatigueRuleState => ({
  id: rule.id,
  name: rule.name,
  maxHoursPerShift: rule.maxHoursPerShift,
  maxHoursPer7Days: rule.maxHoursPer7Days,
  minRestHours: rule.minRestHours,
  isActive: rule.isActive,
})

export const FatigueRules = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { data } = useSelector((state: RootState) => state.fatigue.rules)
  const [state, setState] = useState<IFatigueRuleState>({ ...DEFAULT_FATIGUE_RULE_FORM })
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null)
  const [params] = useState(() => getParams({ location, limit: 30 }))

  useQueryPagination({
    fetchFunctions: [getFatigueRules],
    cleanUpFunctions: [clearFatigueRules],
    params,
  })

  const activeRule = data.find((rule) => rule.isActive)
  const isEditing = selectedRuleId !== null

  const resetForm = () => {
    setState({ ...DEFAULT_FATIGUE_RULE_FORM })
    setSelectedRuleId(null)
    dispatch(clearSingleFatigueRule())
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name, type } = e.target
    setState((prev) => ({ ...prev, [name]: type === 'number' ? Number(value) : value }))
  }

  const handleSave = async () => {
    if (isEditing) {
      const resp = await dispatch(updateFatigueRule({ id: selectedRuleId, state }))
      if (resp.meta.requestStatus === 'fulfilled') {
        resetForm()
      }
      return
    }

    const activeRules = data.filter((rule) => rule.isActive)
    for (const rule of activeRules) {
      await dispatch(
        updateFatigueRule({
          id: rule.id,
          state: mapRuleToForm({ ...rule, isActive: false }),
        }),
      )
    }
    const resp = await dispatch(createFatigueRule(state))
    if (resp.meta.requestStatus === 'fulfilled') {
      resetForm()
    }
  }

  const handleCancel = () => {
    resetForm()
  }

  const handleSelectVersion = (rule: IGetAllFatigueRulesSingleResponse) => {
    setSelectedRuleId(rule.id)
    setState(mapRuleToForm(rule))
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack title='Fatigue Rules' arrow />
        <Flex gap='1rem' alignStart width='100%' style={{ marginTop: '2rem' }}>
          <Container style={{ flex: 2 }}>
            <ContainerHeader padding>
              <Flex column gap='0.25rem'>
                <TitleText weight='bold'>{isEditing ? 'Edit rules' : 'New rule'}</TitleText>
                {!isEditing && activeRule && (
                  <OtherText grey>
                    CURRENTLY ACTIVE {formatDateForDisplay(activeRule.updatedAt)} · #
                    {activeRule.id}
                  </OtherText>
                )}
              </Flex>
            </ContainerHeader>
            <ContainerContent gap='1.5rem'>
              <Input
                name='name'
                labelText='Rule name'
                value={state.name}
                onChange={handleChange}
                placeholder='Enter rule name'
              />
              <Flex column gap='1rem'>
                <TitleText weight='bold'>Working-hour limits</TitleText>
                <ItemsGrid columns={2} gap='1rem'>
                  <Input
                    name='maxHoursPerShift'
                    labelText='Max hours per shift'
                    value={state.maxHoursPerShift}
                    onChange={handleChange}
                    type='number'
                    placeholder='Enter max hours per shift'
                  />
                  <Input
                    name='maxHoursPer7Days'
                    labelText='Max hours per 7 days'
                    value={state.maxHoursPer7Days}
                    onChange={handleChange}
                    type='number'
                    placeholder='Enter max hours per 7 days'
                  />
                </ItemsGrid>
                <Input
                  name='minRestHours'
                  labelText='Min rest between shifts (h)'
                  value={state.minRestHours}
                  onChange={handleChange}
                  type='number'
                  placeholder='Enter min rest between shifts'
                />
              </Flex>

              <Flex gap='1rem' justend width='100%'>
                {isEditing && <CancelBtn onClick={handleCancel} />}
                <Button orange onClick={handleSave}>
                  {isEditing ? 'Update' : 'Save'}
                </Button>
              </Flex>
            </ContainerContent>
          </Container>

          <Container style={{ flex: 1 }}>
            <ContainerHeader padding>
              <TitleText weight='bold'>Rules</TitleText>
            </ContainerHeader>
            <ContainerContent gap='1rem'>
              {data.map((version) => (
                <Container
                  key={version.id}
                  style={{
                    cursor: 'pointer',
                    border: selectedRuleId === version.id ? '1px solid #ff9900' : undefined,
                  }}
                  onClick={() => handleSelectVersion(version)}
                >
                  <ContainerHeader padding>
                    <Flex between width='100%' center>
                      <BodyText weight='600'>
                        #{version.id} · {formatDateForDisplay(version.createdAt)}
                      </BodyText>
                      {version.isActive && (
                        <Badge text='ACTIVE' orange />
                      )}
                    </Flex>
                  </ContainerHeader>
                  <ContainerContent gap='0.5rem'>
                    <OtherText grey>{formatRuleSummary(version)}</OtherText>
                    <OtherText>{version.name}</OtherText>
                  </ContainerContent>
                </Container>
              ))}
            </ContainerContent>
          </Container>
        </Flex>
      </PageWrapper>
    </>
  )
}
